// Cart drawer overlay, progress bar, gift note, live preview, blank card, and Redo insurance.
// Renders once per page from layout/theme.liquid — no per-instance concerns.

declare global {
  interface Window {
    redoShopify?: {
      init?: (opts: { cartSelector: string; widgetContainer: string; cartType: string }) => void;
    };
    Redo?: {
      cart?: {
        init?: (opts: { container: string }) => void;
      };
    };
  }
}

class CartDrawer {
  private initialized = false;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.reopenCartIfNeeded();
    this.initOverlay();
    this.initProgressBar();
    this.initGiftNote();
    this.initGiftNotePreview();
    this.initBlankCardCheckbox();
    this.initRedoInsurance();
  }

  // Re-open cart after a page reload triggered by gift note save
  private reopenCartIfNeeded() {
    if (sessionStorage.getItem("keepCartOpen") === "true") {
      sessionStorage.removeItem("keepCartOpen");
      setTimeout(() => {
        const cartToggle = document.querySelector<HTMLElement>("[data-cart-toggle]");
        if (cartToggle) cartToggle.click();
      }, 100);
    }
  }

  // Sync the [data-cart-overlay] element with cart open/close state.
  // The theme uses `is-open` on [data-cart-dropdown] to toggle the drawer.
  private initOverlay() {
    const overlay = document.querySelector<HTMLElement>("[data-cart-overlay]");
    const cartDrawer = document.querySelector<HTMLElement>("[data-cart-dropdown]");
    if (!overlay || !cartDrawer) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") {
          overlay.classList.toggle("is-visible", cartDrawer.classList.contains("is-open"));
        }
      }
    });

    observer.observe(cartDrawer, { attributes: true });

    overlay.addEventListener("click", () => {
      cartDrawer.classList.remove("is-open");
      document.body.classList.remove("cart-open");
    });
  }

  // Fetch /cart.js and update progress bar + gift note visibility when cart items change
  private initProgressBar() {
    const updateProgressBar = () => {
      const progressText = document.querySelector<HTMLElement>(".cart-progress-bar__text");
      const progressFill = document.querySelector<HTMLElement>(".cart-progress-bar__fill");
      if (!progressText || !progressFill) return;

      fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          const threshold = 20000; // $200 in cents
          const remaining = threshold - cart.total_price;
          const percentage = Math.min((cart.total_price / threshold) * 100, 100);

          progressFill.style.width = percentage + "%";

          if (remaining > 0) {
            const dollars = (remaining / 100).toFixed(2);
            progressText.innerHTML = `You are $${dollars} away from <strong>Free Shipping</strong>`;
          } else {
            progressText.innerHTML =
              "Congratulations, you've unlocked free shipping within the US!";
          }
        })
        .catch(() => {});
    };

    const updateGiftNoteVisibility = () => {
      const section = document.querySelector<HTMLElement>(".cart-gift-note");
      if (!section) return;
      // Gift notes disabled at theme level — keep hidden permanently
      if (section.dataset.giftNotesDisabled !== undefined) return;

      fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          const hasGiftCard = cart.items.some((item: { product_title: string }) =>
            item.product_title.toUpperCase().includes("GIFT CARD")
          );
          section.classList.toggle("is-hidden", hasGiftCard);
        })
        .catch(() => {});
    };

    const updateCart = () => {
      updateProgressBar();
      updateGiftNoteVisibility();
    };

    updateCart();

    const cartItems = document.querySelector("[data-items-holder]");
    if (cartItems) {
      new MutationObserver(updateCart).observe(cartItems, { childList: true, subtree: true });
    }
  }

  // Gift note toggle, add/update via Shopify AJAX API
  private initGiftNote() {
    const toggle = document.querySelector<HTMLElement>("[data-gift-note-toggle]");
    const content = document.querySelector<HTMLElement>(".cart-gift-note__content");
    const addBtn = document.querySelector<HTMLButtonElement>("[data-add-gift-note]");

    if (toggle && content) {
      toggle.addEventListener("click", function () {
        this.classList.toggle("is-active");
        content.style.display = content.style.display === "none" ? "block" : "none";
      });
    }

    if (addBtn) {
      addBtn.addEventListener("click", async function () {
        const variantId = this.dataset.variantId;
        const blankCheckbox = document.querySelector<HTMLInputElement>("[data-blank-card-checkbox]");
        const isBlankCard = blankCheckbox?.checked ?? false;

        const message =
          document.querySelector<HTMLTextAreaElement>("[data-gift-message]")?.value ?? "";
        const from = document.querySelector<HTMLInputElement>("[data-gift-from]")?.value ?? "";
        const to = document.querySelector<HTMLInputElement>("[data-gift-to]")?.value ?? "";

        if (!isBlankCard && !message.trim()) {
          alert('Please enter a gift message or select "Blank card"');
          return;
        }

        this.disabled = true;
        const originalText = this.textContent;
        this.textContent = "ADDING...";

        try {
          const cartResponse = await fetch("/cart.js");
          const cart = await cartResponse.json();
          const existing = cart.items.find(
            (item: { variant_id: number }) => item.variant_id == variantId
          );

          const properties = {
            message: isBlankCard ? "" : message,
            from: isBlankCard ? "" : from,
            to: isBlankCard ? "" : to,
            _blank_card: isBlankCard ? "true" : "false",
          };

          if (existing) {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: existing.key, quantity: 1, properties }),
            });
          } else {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                items: [{ id: parseInt(variantId!), quantity: 1, properties }],
              }),
            });
          }

          // Reload with cart open to show updated state
          sessionStorage.setItem("keepCartOpen", "true");
          document.dispatchEvent(new CustomEvent("cart:updated"));
          document.dispatchEvent(new CustomEvent("theme:cart:change"));
          window.location.reload();
        } catch (error) {
          console.error("Error adding gift note:", error);
          alert("Error adding gift note: " + (error as Error).message);
          this.disabled = false;
          this.textContent = originalText;
        }
      });
    }
  }

  // Live preview: sync gift note input fields to preview card
  private initGiftNotePreview() {
    const messageField = document.querySelector<HTMLTextAreaElement>("[data-gift-message]");
    const fromField = document.querySelector<HTMLInputElement>("[data-gift-from]");
    const toField = document.querySelector<HTMLInputElement>("[data-gift-to]");
    const messagePreview = document.querySelector<HTMLElement>("[data-preview-message]");
    const fromPreview = document.querySelector<HTMLElement>(
      "[data-preview-from] .cart-gift-note__preview-value"
    );
    const toPreview = document.querySelector<HTMLElement>(
      "[data-preview-to] .cart-gift-note__preview-value"
    );

    const placeholder =
      '<span class="cart-gift-note__preview-placeholder">Type your message here</span>';

    if (messageField && messagePreview) {
      messageField.addEventListener("input", function () {
        messagePreview.innerHTML = this.value.trim()
          ? this.value.replace(/\n/g, "<br>")
          : placeholder;
      });
      if (messageField.value.trim()) {
        messagePreview.innerHTML = messageField.value.replace(/\n/g, "<br>");
      }
    }

    if (fromField && fromPreview) {
      fromField.addEventListener("input", function () {
        fromPreview.textContent = this.value || "";
      });
      if (fromField.value) fromPreview.textContent = fromField.value;
    }

    if (toField && toPreview) {
      toField.addEventListener("input", function () {
        toPreview.textContent = this.value || "";
      });
      if (toField.value) toPreview.textContent = toField.value;
    }
  }

  // Blank card checkbox: disable fields and update preview
  private initBlankCardCheckbox() {
    const checkbox = document.querySelector<HTMLInputElement>("[data-blank-card-checkbox]");
    const messageField = document.querySelector<HTMLTextAreaElement>("[data-gift-message]");
    const fromField = document.querySelector<HTMLInputElement>("[data-gift-from]");
    const toField = document.querySelector<HTMLInputElement>("[data-gift-to]");
    const messagePreview = document.querySelector<HTMLElement>("[data-preview-message]");
    const fromPreview = document.querySelector<HTMLElement>(
      "[data-preview-from] .cart-gift-note__preview-value"
    );
    const toPreview = document.querySelector<HTMLElement>(
      "[data-preview-to] .cart-gift-note__preview-value"
    );

    if (!checkbox || !messageField || !fromField || !toField) return;

    checkbox.addEventListener("change", function () {
      const isBlank = this.checked;

      messageField.disabled = isBlank;
      fromField.disabled = isBlank;
      toField.disabled = isBlank;

      if (isBlank) {
        messageField.value = "";
        fromField.value = "";
        toField.value = "";
        if (messagePreview)
          messagePreview.innerHTML =
            '<span class="cart-gift-note__preview-placeholder">Blank card</span>';
        if (fromPreview) fromPreview.textContent = "";
        if (toPreview) toPreview.textContent = "";
      } else {
        if (messagePreview)
          messagePreview.innerHTML =
            '<span class="cart-gift-note__preview-placeholder">Type your message here</span>';
      }
    });
  }

  // Poll for Redo insurance widget and initialize when available
  private initRedoInsurance() {
    const initializeRedo = () => {
      if (window.redoShopify?.init) {
        window.redoShopify.init({
          cartSelector: "#cart-dropdown",
          widgetContainer: "#redo-widget-container",
          cartType: "drawer",
        });
      } else if (window.Redo?.cart?.init) {
        window.Redo.cart.init({ container: "#redo-widget-container" });
      }
    };

    initializeRedo();

    let attempts = 0;
    const interval = setInterval(() => {
      if (window.redoShopify || window.Redo) {
        clearInterval(interval);
        initializeRedo();
      }
      if (++attempts > 20) clearInterval(interval);
    }, 500);

    const cartToggle = document.querySelector<HTMLElement>("[data-cart-toggle]");
    if (cartToggle) {
      cartToggle.addEventListener("click", () => setTimeout(initializeRedo, 300));
    }

    document.addEventListener("cart:updated", () => setTimeout(initializeRedo, 100));
    document.addEventListener("theme:cart:change", () => setTimeout(initializeRedo, 100));
  }
}

export const cartDrawer = {
  init: () => {
    new CartDrawer().init();
  },
};
